import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { CustomPowershellReport } from '../../../../shared/models/generated/ziroModels';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../../button/button.component';
import { CustomPowershellQueriesContext } from '../../../../shared/contexts/custom-powershell-queries.context';
import { Observable, of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { catchError, delayWhen, map, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroCodeAreaConfig, ZiroCodeAreaMode } from '../../../../forms/fields/code-area/ziro-code-area.component';
import { isEqual } from 'lodash';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

interface CustomPowershellReportDatatableRow extends DatatableRow {
  [key: string]: unknown;
}

export interface CustomPowershellQueriesForm {
  pwshQuery: string;
  reportName: string;
}

@Component({
  selector: 'ziro-custom-powershell-queries-form',
  templateUrl: './custom-powershell-queries-form.component.html',
  styleUrls: ['../../../reporting.scss'],
  standalone: false,
})
export class CustomPowershellQueriesFormComponent
  extends ZiroFormAbstractDirective<CustomPowershellReport, CustomPowershellQueriesForm>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _bottomNavService = inject(BottomNavService);
  private _customPowershellReportContext = inject(CustomPowershellQueriesContext);
  private _translateService = inject(TranslateService);
  private _ziroModalService = inject(ZiroModalService);
  private _toastService = inject(ToastService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);

  readonly report = input<CustomPowershellReport>();
  readonly allReportNames = input<string[]>();

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  tableDataAutomation = 'custom-powershell-queries-datatable';
  isLoading = true;
  isQueryRunning = false;
  hasQueryRun = false;
  queryResults: any[] = [];
  columns: DatatableColumn<CustomPowershellReportDatatableRow>[];
  tableData: CustomPowershellReportDatatableRow[];

  formConfig: ZiroFormConfig;

  private _isInvalidQuery = false;
  private _invalidQueryMessage = '';
  private _submitEvent: 'SUBMIT' | 'QUERY' | 'EXPORT';
  private _subscriptions = new Subscription();
  private updatedReport: CustomPowershellReport;

  ngOnInit(): void {
    this.updatedReport = this.report() ? { ...this.report() } : null;
    this._initFormConfig();
    this._initFormData();
    this._initBottomNav();

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<CustomPowershellReport>) => {
      if (data.valid === ZiroFormsValidationState.VALID && this.isFormSubmitted) {
        this._bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
      }
      if (data?.old?.pwshQuery !== data?.new?.pwshQuery) {
        this.hasQueryRun = false;
      }
      if (data.old && !isEqual(data.old, data.new)) {
        this._isInvalidQuery = false;
        this.fieldChannels['pwshQuery'].validateSource.next();
      }
    });
    this._subscriptions.add(formUpdateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  toFormData = (entity: CustomPowershellReport): CustomPowershellQueriesForm => {
    return {
      pwshQuery: entity.pwshQuery,
      reportName: entity.reportName,
    };
  };

  toEntity = (formData: CustomPowershellQueriesForm): CustomPowershellReport => {
    return {
      id: this.updatedReport ? this.updatedReport.id : null,
      lastUpdated: this.updatedReport ? this.updatedReport.lastUpdated : null,
      reportName: formData.reportName,
      pwshQuery: formData.pwshQuery,
    };
  };

  onTestQueryClick(): void {
    this._submitEvent = 'QUERY';
    this._validateAndSubmitSource.next(true);
  }

  private _testQuery(): Observable<boolean> {
    this.queryResults = [];
    this.isQueryRunning = true;
    this.hasQueryRun = false;
    return this._customPowershellReportContext.test(this.entity.pwshQuery).pipe(
      map((response) => {
        this.queryResults = response.results;
        this.hasQueryRun = true;
        this._initTable();
        this.isQueryRunning = false;
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.TABLE,
          'tkey;reports.microsoft.custom_powershell_reports.form.toast.query.success.title',
          'tkey;reports.microsoft.custom_powershell_reports.form.toast.query.success.message',
        );
        this._resetButtonStates();
        return true;
      }),
      catchError((error) => {
        this.isQueryRunning = false;
        this._isInvalidQuery = true;
        this._resetButtonStates();
        if (error.status === 422) {
          this._handleQueryErrorResponse(error);
          return of(false);
        } else {
          throw error;
        }
      }),
    );
  }

  private _saveReport(): Observable<void> {
    const request = this.updatedReport
      ? this._customPowershellReportContext.update({
          ...this.entity,
          id: this.updatedReport.id,
        })
      : this._customPowershellReportContext.create(this.entity);
    return request.pipe(
      map((id) => {
        this.updatedReport = {
          ...this.entity,
          id: id,
        };
        this._toastService.pushSaveToast(
          'tkey;reports.microsoft.custom_powershell_reports.title',
          this.entity.reportName,
          ZiroIcons.TABLE,
        );
      }),
    );
  }

  protected submit(): Observable<void> {
    this._bottomNavService.setBottomNavValidationError(false);
    if (this._submitEvent === 'QUERY') {
      this._submitEvent = null;
      this._setButtonState('custom-powershell-queries-test', true, true);
      this._setButtonState('custom-powershell-queries-export', false, true);
      this._setButtonState('custom-powershell-queries-cancel', false, true);
      this._setButtonState('custom-powershell-queries-delete', false, true);
      this._setButtonState('custom-powershell-queries-save', false, true);
      return this._testQuery().pipe(
        map((isTestSuccessful) => {
          if (isTestSuccessful) {
            setTimeout(() => {
              if (document.getElementById('custom-powershell-queries-results-header')) {
                document
                  .getElementById('custom-powershell-queries-results-header')
                  .scrollIntoView({ behavior: 'smooth' });
              }
            });
          }
        }),
      );
    }
    if (this._submitEvent === 'SUBMIT') {
      this._submitEvent = null;
      this._setButtonState('custom-powershell-queries-test', false, true);
      this._setButtonState('custom-powershell-queries-export', false, true);
      this._setButtonState('custom-powershell-queries-cancel', false, true);
      this._setButtonState('custom-powershell-queries-delete', false, true);
      this._setButtonState('custom-powershell-queries-save', true, true);

      if (!this.hasQueryRun && (!this.updatedReport || this._hasQueryChanged())) {
        return this._showRunQueryConfirmationModal().pipe(
          map((isTestSuccessful) => {
            this._resetButtonStates();
            if (isTestSuccessful) {
              this._returnToList();
            }
          }),
        );
      } else {
        return this._saveReport().pipe(
          tap(() => {
            this._resetButtonStates();
            this._returnToList();
          }),
        );
      }
    }
    if (this._submitEvent === 'EXPORT') {
      this._submitEvent = null;
      if (!this.updatedReport || this._hasQueryChanged()) {
        const options = {
          windowClass: 'custom-report-run-and-save-report-modal',
          modalViewProperties: {
            title: this._translateService.instant('tkey;reports.microsoft.custom_powershell_reports.form.modal.title'),
            promptBody: this._translateService.instant(
              'tkey;reports.microsoft.custom_powershell_reports.form.modal.export.message',
            ),
            icon: ZiroIcons.DOWNLOAD,
            displayCloseButton: true,
            buttons: [
              {
                label: 'tkey;dialogs.button.cancel',
                buttonClass: ButtonStyles.OUTLINE_DEFAULT,
                dataAutomation: 'confirmation-modal-cancel-button',
              },
              {
                label: 'tkey;reports.microsoft.custom_powershell_reports.form.modal.export.button',
                buttonClass: ButtonStyles.OUTLINE_PRIMARY,
                dataAutomation: 'confirmation-modal-confirm-button',
                cb: () => {
                  return this._testQuery().pipe(
                    delayWhen((isTestSuccessful) => {
                      return isTestSuccessful ? this._saveReport() : of(null);
                    }),
                    delayWhen((isTestSuccessful) => {
                      return isTestSuccessful ? this._exportReport() : of(null);
                    }),
                    map(() => {
                      this._initBottomNav();
                      this._ziroFormStateService.setIsFormDirty(false);
                    }),
                  );
                },
              },
            ],
          },
        };
        return this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
      } else {
        return this._exportReport();
      }
    }
  }

  private _showRunQueryConfirmationModal(): Observable<boolean> {
    const options = {
      windowClass: 'custom-powershell-queries-confirmation-modal',
      modalViewProperties: {
        title: this._translateService.instant('tkey;reports.microsoft.custom_powershell_reports.form.modal.title'),
        promptBody: this._translateService.instant(
          'tkey;reports.microsoft.custom_powershell_reports.form.modal.query.message',
        ),
        icon: ZiroIcons.TABLE,
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;reports.microsoft.custom_powershell_reports.form.modal.query.button',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._testQuery().pipe(
                delayWhen((isTestSuccessful) => {
                  return isTestSuccessful ? this._saveReport() : of(null);
                }),
              );
            },
          },
        ],
      },
    };
    return this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _initTable(): void {
    if (this.queryResults.length > 0) {
      this.columns = Object.keys(this.queryResults[0]).map(
        (queryKey) =>
          ({
            name: queryKey,
            label: queryKey,
          }) as DatatableColumn<CustomPowershellReportDatatableRow>,
      );
      this.tableData = this.queryResults;
    }
  }

  private _hasQueryChanged(): boolean {
    return this.updatedReport && this.updatedReport.pwshQuery !== this.entity.pwshQuery;
  }

  private _initFormData(): void {
    if (this.updatedReport) {
      this.entitySource.next(this.updatedReport);
    }
  }

  private _initBottomNav(): void {
    const bottomNavButtons = [
      {
        id: 'custom-powershell-queries-test',
        dataAutomation: 'custom-powershell-queries-test',
        label: 'tkey;reports.microsoft.custom_powershell_reports.bottom_nav.query',
        icon: ZiroIcons.RUN,
        buttonClass: ButtonStyles.INFO,
        cb: () => {
          this._submitEvent = 'QUERY';
          this._validateAndSubmitSource.next(true);
        },
      },
      {
        id: 'custom-powershell-queries-export',
        dataAutomation: 'custom-powershell-queries-export',
        label: 'tkey;reports.microsoft.custom_powershell_reports.bottom_nav.export',
        icon: ZiroIcons.EXPORT,
        buttonClass: ButtonStyles.INFO,
        cb: () => {
          this._submitEvent = 'EXPORT';
          this._validateAndSubmitSource.next(true);
        },
      },
      {
        id: 'custom-powershell-queries-cancel',
        dataAutomation: 'custom-powershell-queries-cancel',
        label: 'tkey;global.button.cancel.text',
        buttonClass: ButtonStyles.DEFAULT,
        cb: () => this._returnToList(),
      },
      {
        id: 'custom-powershell-queries-save',
        dataAutomation: 'custom-powershell-queries-save',
        label: 'tkey;global.button.save.text',
        icon: ZiroIcons.SAVE_SUCCESS,
        buttonClass: ButtonStyles.PRIMARY,
        cb: () => {
          this._submitEvent = 'SUBMIT';
          this._validateAndSubmitSource.next(true);
        },
      },
    ];

    if (this.updatedReport?.id) {
      const deleteButton = {
        id: 'custom-powershell-queries-delete',
        dataAutomation: 'custom-powershell-queries-delete',
        label: 'tkey;global.button.delete.text',
        icon: ZiroIcons.DELETE_SMALL,
        buttonClass: ButtonStyles.DANGER,
        cb: () => this._promptDeleteReport(),
      };
      bottomNavButtons.splice(3, 0, deleteButton);
    }

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _exportReport(): Observable<void> {
    this._setButtonState('custom-powershell-queries-test', false, true);
    this._setButtonState('custom-powershell-queries-export', true, true);
    this._setButtonState('custom-powershell-queries-cancel', false, true);
    this._setButtonState('custom-powershell-queries-delete', false, true);
    this._setButtonState('custom-powershell-queries-save', false, true);
    return this._customPowershellReportContext.export(this.updatedReport.id).pipe(
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.EXPORT,
          'tkey;reports.microsoft.custom_powershell_reports.toast.export.title',
          'tkey;reports.microsoft.custom_powershell_reports.toast.export.message',
        );
        this._resetButtonStates();
      }),
      catchError((err) => {
        this._resetButtonStates();
        throw err;
      }),
    );
  }

  private _promptDeleteReport(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        title: this._translateService.instant('tkey;reports.microsoft.custom_powershell_reports.modal.delete.title'),
        promptBody: this._translateService.instant(
          'tkey;reports.microsoft.custom_powershell_reports.modal.delete.message',
          {
            reportName: this.updatedReport.reportName,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteReport(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteReport(): Observable<void> {
    return this._customPowershellReportContext.delete(this.updatedReport.id).pipe(
      tap(() => {
        this._ziroFormStateService.setIsFormDirty(false);
        this._toastService.pushDeleteToast(
          'tkey;reports.microsoft.custom_powershell_reports.title',
          this.updatedReport.reportName,
        );
        this._returnToList();
      }),
    );
  }

  private _returnToList(): void {
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  private _resetButtonStates(): void {
    this._setButtonState('custom-powershell-queries-test', false, false);
    this._setButtonState('custom-powershell-queries-export', false, false);
    this._setButtonState('custom-powershell-queries-cancel', false, false);
    this._setButtonState('custom-powershell-queries-delete', false, false);
    this._setButtonState('custom-powershell-queries-save', false, false);
  }

  private _setButtonState(buttonId: string, pending: boolean, disabled: boolean, tooltip?: string): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: pending,
          buttonDisableState: { disabled: disabled, tooltipKey: tooltip || '' },
        },
      }),
    );
  }

  private _handleQueryErrorResponse(error: any): void {
    this._bottomNavService.setBottomNavValidationError(true);
    this._invalidQueryMessage = error.error.description;
    this.fieldChannels['pwshQuery'].validateSource.next();
    this._toastService.push(
      ToastTypes.ERROR,
      ZiroIcons.TABLE,
      'tkey;reports.microsoft.custom_powershell_reports.form.toast.query.error.title',
      'tkey;reports.microsoft.custom_powershell_reports.form.toast.query.error.message',
    );
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        reportName: {
          label: 'tkey;reports.custom_reports.custom_report_list.report_name',
          dataAutomation: 'custom-powershell-queries-name',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                if (!this.updatedReport) {
                  return this.allReportNames().includes(val.trim().toLowerCase())
                    ? ZiroFormsValidationState.INVALID
                    : ZiroFormsValidationState.VALID;
                } else {
                  return this.updatedReport.reportName.trim().toLowerCase() === val.trim().toLowerCase() ||
                    !this.allReportNames().includes(val.trim().toLowerCase())
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                }
              },
              message: 'tkey;reports.microsoft.custom_powershell_reports.form.report_name.error',
            },
          ],
        },
        pwshQuery: {
          label: 'tkey;reports.microsoft.custom_powershell_reports.form.pwsh_query.label',
          dataAutomation: 'custom-powershell-queries-query',
          componentConfig: new ZiroCodeAreaConfig({ mode: ZiroCodeAreaMode.POWERSHELL }),
          required: true,
          validation: [
            {
              validator: () =>
                !this._isInvalidQuery ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: () => this._invalidQueryMessage,
            },
          ],
        },
      },
    };
  }
}
